import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import AddTaskRoundedIcon from "@mui/icons-material/AddTaskRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import HourglassTopRoundedIcon from "@mui/icons-material/HourglassTopRounded";
import StickyNote2RoundedIcon from "@mui/icons-material/StickyNote2Rounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import HistoryToggleOffRoundedIcon from "@mui/icons-material/HistoryToggleOffRounded";
import { useTheme } from "@mui/material/styles";
import { ownerApi } from "../../../../api/owner";
import { formatApiError } from "../../../../api/client";
import { convertOwnerInboxToCommitment, recordOwnerInboxRetrospective, resolveOwnerInboxEntry } from "../../../../services/ownerInbox.service";
import { dateTime } from "./ownerCenterUi";
import { OwnerVoiceFormControls, useOwnerVoiceForm, voiceFieldProps } from "../voice/OwnerVoiceOperatorContext";

const NONE_OPTION = "__none__";

const EXECUTION_CONTEXTS = [
  { value: "programming", label: "Programación", aliases: ["programar", "programando", "código", "codigo", "codear"] },
  { value: "home", label: "Casa", aliases: ["hogar", "en casa"] },
  { value: "administration", label: "Administración", aliases: ["administrativo", "administrar", "papeles"] },
  { value: "calls", label: "Llamadas / mensajes", aliases: ["llamadas", "mensajes", "whatsapp", "contactos"] },
  { value: "errands", label: "Trámites / calle", aliases: ["tramites", "trámites", "calle", "mandados"] },
  { value: "study", label: "Estudio", aliases: ["estudiar", "facultad", "curso"] },
  { value: "personal", label: "Personal", aliases: ["yo", "personal"] },
  { value: "work", label: "Trabajo general", aliases: ["trabajo", "trabajar"] },
  { value: "other", label: "Otro", aliases: ["otro"] },
];

function suggestedExecutionContext(text = "") {
  const value = String(text || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  if (/\b(?:codigo|codear|programar|programacion|deploy|frontend|backend|login|api|bug|fix|web)\b/.test(value)) return "programming";
  if (/\b(?:casa|limpiar|ordenar|cocinar|lavar|compras del hogar)\b/.test(value)) return "home";
  if (/\b(?:llamar|mensaje|whatsapp|responder|contactar|escribirle)\b/.test(value)) return "calls";
  if (/\b(?:tramite|banco|correo|retirar|llevar|buscar|calle)\b/.test(value)) return "errands";
  if (/\b(?:estudiar|facultad|leer|examen|curso|clase)\b/.test(value)) return "study";
  if (/\b(?:factura|cobro|pago|administrar|presupuesto|documentacion|documento)\b/.test(value)) return "administration";
  return "";
}

const ACTIONS = [
  { key: "action", label: "Tomar", icon: <AddTaskRoundedIcon fontSize="small" />, helper: "Convertí la nota en una tarea ejecutable y elegí en qué bloque tiene sentido hacerla. No le inventamos fecha ni duración." },
  { key: "link", label: "Proyecto / persona", icon: <LinkRoundedIcon fontSize="small" />, helper: "La guarda como contexto vinculado, sin inventar una tarea." },
  { key: "waiting", label: "Esperar", icon: <HourglassTopRoundedIcon fontSize="small" />, helper: "La convierte en una espera real y pide qué falta." },
  { key: "note", label: "Nota", icon: <StickyNote2RoundedIcon fontSize="small" />, helper: "La conserva como nota resuelta." },
  { key: "schedule", label: "Agendar", icon: <EventAvailableRoundedIcon fontSize="small" />, helper: "La convierte en un recordatorio con fecha y hora." },
  { key: "dismiss", label: "Descartar", icon: <DeleteOutlineRoundedIcon fontSize="small" />, helper: "La saca del Inbox dejando registro de la decisión." },
];

function idOf(value) {
  return String(value?.id || value?._id || "");
}

function interpretationOf(entry) {
  const value = entry?.structuredData?.captureInterpretation;
  return value && typeof value === "object" ? value : null;
}

function uniqueCandidateId(items = []) {
  return items.length === 1 ? idOf(items[0]) : "";
}

function mergeOptions(primary = [], candidates = []) {
  const map = new Map();
  [...primary, ...candidates].forEach((item) => {
    const id = idOf(item);
    if (id && !map.has(id)) map.set(id, item);
  });
  return [...map.values()];
}

function localDateTimeInput(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 16);
  const pad = (number) => String(number).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export default function OwnerInboxProcessorDialog({
  open,
  onClose,
  focusEntryId = "",
  entries = [],
  totalCount = 0,
  initiatives = [],
  people = [],
  onChanged,
}) {
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [processedIds, setProcessedIds] = React.useState(() => new Set());
  const visibleEntries = React.useMemo(() => {
    const rows = entries.filter((item) => !processedIds.has(idOf(item)));
    if (!focusEntryId) return rows;
    const index = rows.findIndex((item) => idOf(item) === String(focusEntryId));
    if (index <= 0) return rows;
    return [rows[index], ...rows.slice(0, index), ...rows.slice(index + 1)];
  }, [entries, focusEntryId, processedIds]);
  const entry = visibleEntries[0] || null;
  const entryId = idOf(entry);
  const interpretation = interpretationOf(entry);
  const candidatePeople = interpretation?.candidates?.people || [];
  const candidateProjects = interpretation?.candidates?.projects || [];
  const retrospective = interpretation?.retrospective || null;
  const retrospectivePeople = retrospective?.candidates?.people || [];
  const retrospectiveProjects = retrospective?.candidates?.projects || [];

  const [mode, setMode] = React.useState("");
  const [projectId, setProjectId] = React.useState("");
  const [clientId, setClientId] = React.useState("");
  const [availablePeople, setAvailablePeople] = React.useState(people);
  const [waitingFor, setWaitingFor] = React.useState("");
  const [reviewAt, setReviewAt] = React.useState("");
  const [scheduledAt, setScheduledAt] = React.useState("");
  const [actionTitle, setActionTitle] = React.useState("");
  const [actionExecutionContext, setActionExecutionContext] = React.useState("");
  const [actionProjectId, setActionProjectId] = React.useState("");
  const [actionClientId, setActionClientId] = React.useState("");
  const [retroMinutes, setRetroMinutes] = React.useState("");
  const [retroOccurredAt, setRetroOccurredAt] = React.useState("");
  const [retroProjectId, setRetroProjectId] = React.useState("");
  const [retroClientId, setRetroClientId] = React.useState("");
  const [confirmDiscard, setConfirmDiscard] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");

  const projectOptions = React.useMemo(
    () => mergeOptions(mergeOptions(initiatives, candidateProjects), retrospectiveProjects),
    [initiatives, candidateProjects, retrospectiveProjects],
  );
  const peopleOptions = React.useMemo(
    () => mergeOptions(mergeOptions(availablePeople, candidatePeople), retrospectivePeople),
    [availablePeople, candidatePeople, retrospectivePeople],
  );

  React.useEffect(() => {
    setProcessedIds((current) => {
      const present = new Set(entries.map(idOf));
      const next = new Set([...current].filter((id) => present.has(id)));
      return next.size === current.size ? current : next;
    });
  }, [entries]);

  React.useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    ownerApi.listClients({ limit: 250 })
      .then((response) => { if (!cancelled) setAvailablePeople(response?.clients || people); })
      .catch(() => { if (!cancelled) setAvailablePeople(people); });
    return () => { cancelled = true; };
  }, [open, people]);

  React.useEffect(() => {
    const date = interpretation?.date || null;
    const suggestedProjectId = uniqueCandidateId(candidateProjects);
    const suggestedClientId = uniqueCandidateId(candidatePeople);
    const suggestedRetroProjectId = uniqueCandidateId(retrospectiveProjects);
    const suggestedRetroClientId = uniqueCandidateId(retrospectivePeople);

    setMode(retrospective ? "retrospective" : "");
    setProjectId(suggestedProjectId);
    setClientId(suggestedClientId);
    setWaitingFor("");
    setReviewAt("");
    setScheduledAt(date?.localDateTime || "");
    setActionTitle(entry?.text || "");
    setActionExecutionContext(suggestedExecutionContext(entry?.text || ""));
    setActionProjectId(suggestedProjectId);
    setActionClientId(suggestedClientId);
    setRetroMinutes(retrospective?.duration?.minutes ? String(retrospective.duration.minutes) : "");
    setRetroOccurredAt(localDateTimeInput(retrospective?.occurredAt?.iso || entry?.occurredAt));
    setRetroProjectId(suggestedRetroProjectId || (retrospectiveProjects.length ? "" : NONE_OPTION));
    setRetroClientId(suggestedRetroClientId || (retrospectivePeople.length ? "" : NONE_OPTION));
    setConfirmDiscard(false);
    setError("");
  }, [entryId, open]); // eslint-disable-line react-hooks/exhaustive-deps

  const resolveAndAdvance = async (work, scopes = ["today"]) => {
    if (!entry || busy) return false;
    setBusy(true);
    setError("");
    try {
      await work();
      setProcessedIds((current) => new Set([...current, entryId]));
      await onChanged?.(scopes);
      return true;
    } catch (err) {
      setError(formatApiError(err, "No pude procesar esta entrada."));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const runQuickAction = (key) => {
    if (!entry || busy) return;
    if (["action", "link", "waiting", "schedule"].includes(key)) {
      setMode((current) => current === key ? "" : key);
      setConfirmDiscard(false);
      setError("");
      return;
    }
    if (key === "dismiss" && !confirmDiscard) {
      setConfirmDiscard(true);
      setMode("");
      setError("");
      return;
    }
    if (key === "note") {
      resolveAndAdvance(
        () => resolveOwnerInboxEntry(ownerApi, entry, {
          kind: "note",
          entryKind: "note",
          entityType: "OwnerLifeEntry",
          entityId: entryId,
          note: "Guardado como nota desde el procesador del Inbox.",
        }),
        ["today"],
      );
      return;
    }
    if (key === "dismiss") {
      resolveAndAdvance(
        () => resolveOwnerInboxEntry(ownerApi, entry, {
          status: "dismissed",
          kind: "dismissed",
          entityType: "OwnerLifeEntry",
          entityId: entryId,
          note: "Descartado conscientemente desde el procesador del Inbox.",
        }),
        ["today"],
      );
    }
  };

  const saveRetrospective = () => {
    if (!retrospective?.intent) return;
    if (retrospective.intent === "work") {
      const minutes = Number(retroMinutes);
      if (!Number.isFinite(minutes) || minutes < 1 || minutes > 1440) {
        setError("Indicá cuántos minutos reales trabajaste.");
        return;
      }
      if (retrospectiveProjects.length > 1 && !retroProjectId) {
        setError("Elegí cuál de los proyectos candidatos corresponde, o seleccioná Sin proyecto.");
        return;
      }
      if (retrospectivePeople.length > 1 && !retroClientId) {
        setError("Elegí cuál de las personas candidatas corresponde, o seleccioná Sin persona.");
        return;
      }
    }
    if (retrospective.intent === "interaction" && (!retroClientId || retroClientId === NONE_OPTION)) {
      setError("Elegí con quién fue la interacción.");
      return;
    }
    if (!retroOccurredAt) {
      setError("Indicá cuándo ocurrió el hecho.");
      return;
    }

    const scopes = retrospective.intent === "work"
      ? ["today", "calendar", "projects", "people"]
      : ["today", "people"];
    resolveAndAdvance(async () => {
      const response = await recordOwnerInboxRetrospective(ownerApi, entry, {
        intent: retrospective.intent,
        occurredAt: retroOccurredAt,
        ...(retrospective.intent === "work" ? {
          minutes: Number(retroMinutes),
          initiativeId: retroProjectId === NONE_OPTION ? null : (retroProjectId || null),
          clientId: retroClientId === NONE_OPTION ? null : (retroClientId || null),
        } : { clientId: retroClientId }),
      });
      if (!response?.resolved) throw new Error(response?.message || "Falta confirmar un dato antes de registrar este hecho.");
      return response;
    }, scopes);
  };

  const saveAction = () => {
    const title = actionTitle.trim();
    if (!title) {
      setError("La acción necesita un texto concreto.");
      return;
    }
    if (!actionExecutionContext) {
      setError("Elegí en qué bloque o contexto tiene sentido hacer esta tarea.");
      return;
    }

    return resolveAndAdvance(
      () => convertOwnerInboxToCommitment(ownerApi, entry, {
        title,
        kind: "task",
        initiativeId: actionProjectId || null,
        clientId: actionClientId || null,
        work: { state: "ready", executionContext: actionExecutionContext },
        notes: "Tomado como tarea desde una nota mental del Inbox.",
      }),
      ["today", "commitments", "calendar", ...(actionProjectId ? ["projects"] : []), ...(actionClientId ? ["people"] : [])],
    );
  };

  const saveLink = () => {
    if (!projectId && !clientId) {
      setError("Elegí al menos un proyecto o una persona.");
      return;
    }
    const project = projectOptions.find((item) => idOf(item) === projectId);
    const person = peopleOptions.find((item) => idOf(item) === clientId);
    const labels = [project?.name, person?.name].filter(Boolean).join(" · ");
    resolveAndAdvance(
      () => resolveOwnerInboxEntry(ownerApi, entry, {
        kind: "linked",
        entryKind: "note",
        entityType: projectId ? "OwnerInitiative" : "OwnerClient",
        entityId: projectId || clientId,
        initiativeId: projectId,
        clientId,
        note: `Guardado como contexto${labels ? ` de ${labels}` : ""}.`,
      }),
      ["today", "projects", "people"],
    );
  };

  const saveWaiting = () => {
    const context = waitingFor.trim();
    if (!context) {
      setError("Decime qué estás esperando.");
      return;
    }
    if (!clientId) {
      setError("Elegí de quién estás esperando esto.");
      return;
    }
    if (!reviewAt) {
      setError("Definí cuándo querés volver a revisar la espera.");
      return;
    }
    resolveAndAdvance(
      () => convertOwnerInboxToCommitment(ownerApi, entry, {
        kind: "follow_up",
        clientId,
        work: { state: "waiting", waitingFor: context, reviewAt },
        notes: "Creado como espera a tercero desde el procesador del Inbox.",
      }),
      ["today", "commitments", "waiting", "calendar"],
    );
  };

  const saveSchedule = () => {
    if (!scheduledAt) {
      setError("Elegí cuándo querés volver a ver esto.");
      return;
    }
    resolveAndAdvance(
      () => convertOwnerInboxToCommitment(ownerApi, entry, {
        kind: "reminder",
        scheduledStartAt: scheduledAt,
        notes: "Agendado desde el procesador del Inbox.",
      }),
      ["today", "commitments", "calendar"],
    );
  };

  useOwnerVoiceForm({
    id: "inbox-action",
    open: Boolean(open && entry && mode === "action"),
    config: {
      title: "tomar nota como tarea",
      values: { actionTitle, executionContext: actionExecutionContext },
      setValue: (name, value) => {
        if (name === "actionTitle") setActionTitle(value);
        if (name === "executionContext") setActionExecutionContext(value);
      },
      fields: [
        { name: "actionTitle", label: "tarea", prompt: "¿Qué tarea querés tomar?", kind: "text", required: true, skipIfFilled: true },
        { name: "executionContext", label: "bloque", prompt: "¿En qué bloque entra mejor? Por ejemplo Programación, Casa, Administración, Llamadas, Trámites, Estudio o Personal.", kind: "choice", required: true, announceCurrent: true, options: EXECUTION_CONTEXTS },
      ],
      onSubmit: saveAction,
      onCancel: () => setMode(""),
      successMessage: "Tarea tomada. Quedó disponible en el bloque elegido, sin fecha límite inventada.",
    },
  });

  const processedStillPresent = entries.filter((item) => processedIds.has(idOf(item))).length;
  const remaining = Math.max(Number(totalCount || entries.length || 0) - processedStillPresent, visibleEntries.length, 0);

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullScreen={mobile}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: { xs: 0, sm: 3 }, overflow: "hidden" } }}
    >
      <DialogTitle sx={{ pb: .8 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Box>
            <Typography sx={{ fontWeight: 1000, fontSize: { xs: "1.05rem", sm: "1.15rem" } }}>Procesar Inbox</Typography>
            <Typography sx={{ color: "text.secondary", fontSize: ".78rem", fontWeight: 750 }}>Una decisión por vez. La interpretación siempre se puede corregir.</Typography>
          </Box>
          <Chip size="small" label={remaining ? `${remaining} pendientes` : "Limpio"} sx={{ fontWeight: 950 }} />
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ px: { xs: 1.4, sm: 2.4 }, py: 1.2 }}>
        {error ? <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert> : null}

        {entry ? (
          <Stack spacing={1.2}>
            <Box sx={{ p: { xs: 1.35, sm: 1.6 }, border: "1px solid", borderColor: "divider", borderRadius: 2.5, bgcolor: "background.default" }}>
              <Typography sx={{ fontSize: { xs: "1rem", sm: "1.06rem" }, fontWeight: 950, whiteSpace: "pre-wrap", lineHeight: 1.35 }}>{entry.text}</Typography>
              <Typography sx={{ mt: .65, color: "text.secondary", fontSize: ".72rem", fontWeight: 750 }}>{dateTime(entry.occurredAt)} · {entry.inputSource || "text"}</Typography>
            </Box>

            {interpretation?.hasSuggestions ? (
              <Box sx={{ p: 1.15, borderRadius: 2.3, border: "1px solid", borderColor: "info.light", bgcolor: "action.hover" }}>
                <Stack direction="row" spacing={.7} alignItems="center" sx={{ mb: .75 }}>
                  <AutoAwesomeRoundedIcon fontSize="small" />
                  <Typography sx={{ fontSize: ".82rem", fontWeight: 1000 }}>Interpretación propuesta</Typography>
                </Stack>
                <Stack direction="row" gap={.55} flexWrap="wrap">
                  {interpretation?.date ? <Chip size="small" label={`Fecha: ${interpretation.date.label || interpretation.date.dateKey}`} /> : null}
                  {interpretation?.date?.time ? <Chip size="small" label={`Hora: ${interpretation.date.time}`} /> : null}
                  {interpretation?.duration?.minutes ? <Chip size="small" label={`Duración: ${interpretation.duration.minutes} min`} /> : null}
                  {candidateProjects.map((item) => <Chip key={`project-${idOf(item)}`} size="small" label={`Proyecto candidato: ${item.name}`} />)}
                  {candidatePeople.map((item) => <Chip key={`person-${idOf(item)}`} size="small" label={`Persona candidata: ${item.name}`} />)}
                </Stack>
                <Typography sx={{ mt: .7, color: "text.secondary", fontSize: ".72rem", fontWeight: 750 }}>
                  Es una propuesta derivada del texto original. No crea vínculos ni prioridades hasta que vos confirmes una resolución.
                </Typography>
              </Box>
            ) : null}

            {retrospective ? (
              <Box sx={{ p: 1.15, borderRadius: 2.3, border: "1px solid", borderColor: "success.light", bgcolor: "action.hover" }}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={.8} alignItems={{ xs: "stretch", sm: "center" }}>
                  <Stack direction="row" spacing={.7} alignItems="center" sx={{ flex: 1 }}>
                    <HistoryToggleOffRoundedIcon fontSize="small" />
                    <Box>
                      <Typography sx={{ fontSize: ".84rem", fontWeight: 1000 }}>Esto parece un hecho pasado</Typography>
                      <Typography sx={{ color: "text.secondary", fontSize: ".72rem", fontWeight: 750 }}>
                        {retrospective.intent === "work" ? "Registralo como trabajo real; no hace falta convertirlo en una tarea futura." : "Registralo como interacción pasada; no se crea seguimiento salvo que después lo decidas."}
                      </Typography>
                    </Box>
                  </Stack>
                  <Button variant={mode === "retrospective" ? "contained" : "outlined"} onClick={() => setMode((current) => current === "retrospective" ? "" : "retrospective")} disabled={busy} sx={{ textTransform: "none", fontWeight: 950, minHeight: 40 }}>
                    {retrospective.intent === "work" ? "Registrar trabajo" : "Registrar interacción"}
                  </Button>
                </Stack>
              </Box>
            ) : null}

            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: .75 }}>
              {ACTIONS.map((action) => (
                <Button
                  key={action.key}
                  variant={mode === action.key || (action.key === "dismiss" && confirmDiscard) ? "contained" : "outlined"}
                  startIcon={action.icon}
                  disabled={busy}
                  onClick={() => runQuickAction(action.key)}
                  sx={{ minHeight: 48, justifyContent: "flex-start", textTransform: "none", fontWeight: 950, borderRadius: 2.2, px: 1.15 }}
                >
                  {action.label}
                </Button>
              ))}
            </Box>

            {mode && mode !== "retrospective" ? <Typography sx={{ color: "text.secondary", fontSize: ".76rem", fontWeight: 750 }}>{ACTIONS.find((item) => item.key === mode)?.helper}</Typography> : null}

            {mode === "retrospective" && retrospective ? (
              <Stack spacing={1} sx={{ p: 1.15, borderRadius: 2.3, bgcolor: "action.hover" }}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <TextField fullWidth size="small" type="datetime-local" label="Cuándo ocurrió" InputLabelProps={{ shrink: true }} value={retroOccurredAt} onChange={(event) => setRetroOccurredAt(event.target.value)} />
                  {retrospective.intent === "work" ? <TextField fullWidth size="small" type="number" label="Minutos reales" value={retroMinutes} onChange={(event) => setRetroMinutes(event.target.value)} inputProps={{ min: 1, max: 1440, step: 1 }} /> : null}
                </Stack>
                {retrospective.intent === "work" ? (
                  <>
                    <FormControl fullWidth size="small">
                      <InputLabel>Proyecto (opcional)</InputLabel>
                      <Select label="Proyecto (opcional)" value={retroProjectId} onChange={(event) => setRetroProjectId(event.target.value)}>
                        <MenuItem value={NONE_OPTION}>Sin proyecto</MenuItem>
                        {projectOptions.map((item) => <MenuItem key={idOf(item)} value={idOf(item)}>{item.name}</MenuItem>)}
                      </Select>
                    </FormControl>
                    <FormControl fullWidth size="small">
                      <InputLabel>Persona (opcional)</InputLabel>
                      <Select label="Persona (opcional)" value={retroClientId} onChange={(event) => setRetroClientId(event.target.value)}>
                        <MenuItem value={NONE_OPTION}>Sin persona</MenuItem>
                        {peopleOptions.map((item) => <MenuItem key={idOf(item)} value={idOf(item)}>{item.name}{item.company ? ` · ${item.company}` : ""}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </>
                ) : (
                  <FormControl fullWidth size="small">
                    <InputLabel>Persona</InputLabel>
                    <Select label="Persona" value={retroClientId} onChange={(event) => setRetroClientId(event.target.value)}>
                      <MenuItem value="">Elegir persona</MenuItem>
                      {peopleOptions.map((item) => <MenuItem key={idOf(item)} value={idOf(item)}>{item.name}{item.company ? ` · ${item.company}` : ""}</MenuItem>)}
                    </Select>
                  </FormControl>
                )}
                <Stack direction="row" gap={.55} flexWrap="wrap">
                  {retrospective.intent === "interaction" ? <Chip size="small" label={`Tipo: ${retrospective?.interaction?.kind || "conversation"}`} /> : null}
                  {retrospective.intent === "interaction" ? <Chip size="small" label={`Canal: ${retrospective?.interaction?.channel || "other"}`} /> : null}
                  {retrospective?.occurredAt?.precision === "date" ? <Chip size="small" label="Hora aproximada" /> : null}
                </Stack>
                <Typography sx={{ color: "text.secondary", fontSize: ".7rem", fontWeight: 720 }}>
                  Guardamos el texto original como trazabilidad. Los vínculos sólo se aplican con los valores visibles acá.
                </Typography>
                <Button variant="contained" endIcon={<ArrowForwardRoundedIcon />} disabled={busy} onClick={saveRetrospective} sx={{ textTransform: "none", fontWeight: 950 }}>Registrar hecho y seguir</Button>
              </Stack>
            ) : null}

            {mode === "action" ? (
              <Stack spacing={1} sx={{ p: 1.15, borderRadius: 2.3, bgcolor: "action.hover" }}>
                <TextField inputProps={voiceFieldProps("actionTitle")} autoFocus fullWidth size="small" label="Acción" value={actionTitle} onChange={(event) => setActionTitle(event.target.value)} />
                <FormControl fullWidth size="small">
                  <InputLabel>Bloque / contexto</InputLabel>
                  <Select inputProps={voiceFieldProps("executionContext")} label="Bloque / contexto" value={actionExecutionContext} onChange={(event) => setActionExecutionContext(event.target.value)}>
                    <MenuItem value="">Elegir bloque</MenuItem>
                    {EXECUTION_CONTEXTS.map((item) => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small">
                  <InputLabel>Proyecto candidato (opcional)</InputLabel>
                  <Select label="Proyecto candidato (opcional)" value={actionProjectId} onChange={(event) => setActionProjectId(event.target.value)}>
                    <MenuItem value="">Sin proyecto</MenuItem>
                    {projectOptions.map((item) => <MenuItem key={idOf(item)} value={idOf(item)}>{item.name}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small">
                  <InputLabel>Persona candidata (opcional)</InputLabel>
                  <Select label="Persona candidata (opcional)" value={actionClientId} onChange={(event) => setActionClientId(event.target.value)}>
                    <MenuItem value="">Sin persona</MenuItem>
                    {peopleOptions.map((item) => <MenuItem key={idOf(item)} value={idOf(item)}>{item.name}{item.company ? ` · ${item.company}` : ""}</MenuItem>)}
                  </Select>
                </FormControl>
                <Typography sx={{ color: "text.secondary", fontSize: ".7rem", fontWeight: 720 }}>
                  Tomar la nota no la agenda ni crea un vencimiento. Sólo la vuelve ejecutable dentro del bloque elegido; los vínculos siguen siendo opcionales.
                </Typography>
                <OwnerVoiceFormControls label="Tomar hablando" />
                <Button variant="contained" endIcon={<ArrowForwardRoundedIcon />} disabled={busy} onClick={saveAction} sx={{ textTransform: "none", fontWeight: 950 }}>Tomar tarea y seguir</Button>
              </Stack>
            ) : null}

            {mode === "link" ? (
              <Stack spacing={1} sx={{ p: 1.15, borderRadius: 2.3, bgcolor: "action.hover" }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Proyecto (opcional)</InputLabel>
                  <Select label="Proyecto (opcional)" value={projectId} onChange={(event) => setProjectId(event.target.value)}>
                    <MenuItem value="">Sin proyecto</MenuItem>
                    {projectOptions.map((item) => <MenuItem key={idOf(item)} value={idOf(item)}>{item.name}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small">
                  <InputLabel>Persona (opcional)</InputLabel>
                  <Select label="Persona (opcional)" value={clientId} onChange={(event) => setClientId(event.target.value)}>
                    <MenuItem value="">Sin persona</MenuItem>
                    {peopleOptions.map((item) => <MenuItem key={idOf(item)} value={idOf(item)}>{item.name}{item.company ? ` · ${item.company}` : ""}</MenuItem>)}
                  </Select>
                </FormControl>
                <Button variant="contained" endIcon={<ArrowForwardRoundedIcon />} disabled={busy} onClick={saveLink} sx={{ textTransform: "none", fontWeight: 950 }}>Guardar como contexto</Button>
              </Stack>
            ) : null}

            {mode === "waiting" ? (
              <Stack spacing={1} sx={{ p: 1.15, borderRadius: 2.3, bgcolor: "action.hover" }}>
                <TextField autoFocus fullWidth size="small" label="¿Qué estás esperando?" placeholder="Ej.: las credenciales de Memorial" value={waitingFor} onChange={(event) => setWaitingFor(event.target.value)} />
                <FormControl fullWidth size="small">
                  <InputLabel>De quién</InputLabel>
                  <Select label="De quién" value={clientId} onChange={(event) => setClientId(event.target.value)}>
                    <MenuItem value="">Elegir persona</MenuItem>
                    {peopleOptions.map((item) => <MenuItem key={idOf(item)} value={idOf(item)}>{item.name}{item.company ? ` · ${item.company}` : ""}</MenuItem>)}
                  </Select>
                </FormControl>
                <TextField required fullWidth size="small" type="datetime-local" label="Volver a revisar" helperText="Si para entonces no llegó, Owner lo vuelve a poner en atención." InputLabelProps={{ shrink: true }} value={reviewAt} onChange={(event) => setReviewAt(event.target.value)} />
                <Button variant="contained" endIcon={<ArrowForwardRoundedIcon />} disabled={busy} onClick={saveWaiting} sx={{ textTransform: "none", fontWeight: 950 }}>Dejar esperando</Button>
              </Stack>
            ) : null}

            {mode === "schedule" ? (
              <Stack spacing={1} sx={{ p: 1.15, borderRadius: 2.3, bgcolor: "action.hover" }}>
                {!scheduledAt && interpretation?.date?.dateKey ? (
                  <Typography sx={{ color: "text.secondary", fontSize: ".74rem", fontWeight: 760 }}>
                    Detecté {interpretation.date.label || interpretation.date.dateKey}, pero no una hora explícita. Elegila antes de agendar.
                  </Typography>
                ) : null}
                <TextField autoFocus fullWidth size="small" type="datetime-local" label="Cuándo" InputLabelProps={{ shrink: true }} value={scheduledAt} onChange={(event) => setScheduledAt(event.target.value)} />
                <Button variant="contained" endIcon={<ArrowForwardRoundedIcon />} disabled={busy} onClick={saveSchedule} sx={{ textTransform: "none", fontWeight: 950 }}>Agendar y seguir</Button>
              </Stack>
            ) : null}

            {confirmDiscard ? (
              <Stack direction={{ xs: "column", sm: "row" }} spacing={.8} alignItems={{ xs: "stretch", sm: "center" }} sx={{ p: 1.1, borderRadius: 2.3, bgcolor: "action.hover" }}>
                <Typography sx={{ flex: 1, fontSize: ".8rem", fontWeight: 850 }}>¿Descartar esta entrada? La decisión queda registrada.</Typography>
                <Button size="small" onClick={() => setConfirmDiscard(false)} disabled={busy} sx={{ textTransform: "none", fontWeight: 900 }}>No</Button>
                <Button size="small" variant="contained" onClick={() => runQuickAction("dismiss")} disabled={busy} sx={{ textTransform: "none", fontWeight: 950 }}>Sí, descartar</Button>
              </Stack>
            ) : null}

            {busy ? <Typography sx={{ textAlign: "center", color: "text.secondary", fontSize: ".78rem", fontWeight: 800 }}>Guardando decisión…</Typography> : null}
          </Stack>
        ) : (
          <Box sx={{ py: { xs: 6, sm: 5 }, textAlign: "center" }}>
            <Typography sx={{ fontSize: "1.08rem", fontWeight: 1000 }}>Inbox limpio</Typography>
            <Typography sx={{ mt: .4, color: "text.secondary", fontSize: ".82rem" }}>No queda nada pendiente de procesar.</Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: { xs: 1.4, sm: 2.4 }, pb: { xs: 1.3, sm: 1.8 } }}>
        <Button onClick={onClose} disabled={busy} sx={{ textTransform: "none", fontWeight: 900 }}>{entry ? "Cerrar" : "Listo"}</Button>
      </DialogActions>
    </Dialog>
  );
}
